'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import TableOfContents from '@/components/TableOfContents';
import CTACard from '@/components/common/CTACard';
import { marked } from 'marked';
import type { ContentPage } from '@/lib/supabase';
import { extractFaqs, buildFaqJsonLd } from '@/lib/extractFaqs';
import { processHtml } from '@/lib/htmlPostProcess';
import { buildHreflangLinks } from '@/lib/hreflang';
import { trackEvent } from '@/lib/analytics';

interface BlogLayoutProps {
  page: ContentPage;
  relatedPosts?: ContentPage[];
  localeVariants?: { slug: string }[];
}

interface AuthorMeta {
  name: string;
  bio: string;
  initials: string;
  jobTitle: string;
  linkedinUrl?: string;
  slug?: string;
}

const AUTHORS: Record<string, AuthorMeta> = {
  'IntelGrader Team': {
    name: 'IntelGrader Team',
    bio: 'Collective insights from the IntelGrader team. We are building AI-powered grading and assessment tools to give teachers back the hours they lose to marking.',
    initials: 'IG',
    jobTitle: 'Editorial team at IntelGrader',
  },
  'Umang Agarwal': {
    name: 'Umang Agarwal',
    bio: 'Co-Founder at IntelGrader. Ex-P&G, IIM Calcutta. Focused on product and business development for AI-powered education tools.',
    initials: 'UA',
    jobTitle: 'Co-Founder, BD & Product',
    linkedinUrl: 'https://in.linkedin.com/in/umang-agarwal-iimcal',
    slug: 'umang-agarwal',
  },
  'Rohan Prakash': {
    name: 'Rohan Prakash',
    bio: 'Co-Founder at IntelGrader. Ex-Tata, IIM Calcutta, IIT Delhi. Leading product and technology for AI grading systems.',
    initials: 'RP',
    jobTitle: 'Co-Founder, Product & Technology',
    linkedinUrl: 'https://in.linkedin.com/in/rohan-prakash-6a035b179',
    slug: 'rohan-prakash',
  },
  'Kunal Gupta': {
    name: 'Kunal Gupta',
    bio: 'Co-Founder at IntelGrader. Ex-BCG, XLRI. Driving strategy and operations for AI-powered education platforms.',
    initials: 'KG',
    jobTitle: 'Co-Founder, Strategy & Ops',
    linkedinUrl: 'https://in.linkedin.com/in/kunal-gupta-50513b169',
    slug: 'kunal-gupta',
  },
};

function extractTldr(markdown: string): { tldr: string | null; body: string } {
  const match = markdown.match(/^>\s*\*\*TL;DR[^*]*\*\*[:\s]*([\s\S]*?)(?=\n\n(?!>))/m);
  if (!match) return { tldr: null, body: markdown };

  const tldrText = match[1]
    .replace(/^>\s*/gm, '')
    .trim();
  const body = markdown.replace(match[0], '').trim();
  return { tldr: tldrText, body };
}

const BlogLayout: React.FC<BlogLayoutProps> = ({ page, relatedPosts = [], localeVariants = [] }) => {
  const [readProgress, setReadProgress] = useState(0);

  const { tldr, body: bodyWithoutTldr } = useMemo(
    () => extractTldr(page.body_markdown),
    [page.body_markdown]
  );

  const contentHtml = useMemo(
    () => processHtml(marked.parse(bodyWithoutTldr) as string),
    [bodyWithoutTldr]
  );

  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        setReadProgress(Math.min(scrollTop / docHeight, 1));
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const publishDate = page.published_at
    ? new Date(page.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  const author = AUTHORS[page.author || 'IntelGrader Team'] || AUTHORS['IntelGrader Team'];

  // Author Person schema (separate so AI tools resolve identity)
  const personLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: author.name,
    jobTitle: author.jobTitle,
    description: author.bio,
    url: author.slug ? `https://intelgrader.com/team#${author.slug}` : 'https://intelgrader.com/team',
    ...(author.linkedinUrl ? { sameAs: [author.linkedinUrl] } : {}),
    worksFor: {
      '@type': 'Organization',
      name: 'IntelGrader',
      url: 'https://intelgrader.com',
    },
  }), [author]);

  const jsonLd = useMemo(() => {
    const schemaType = page.schema_type || 'Article';

    // Common author object used across schema types
    const authorObj = {
      '@type': 'Person',
      name: author.name,
      jobTitle: author.jobTitle,
      url: author.slug ? `https://intelgrader.com/team#${author.slug}` : 'https://intelgrader.com/team',
      ...(author.linkedinUrl ? { sameAs: [author.linkedinUrl] } : {}),
      worksFor: { '@type': 'Organization', name: 'IntelGrader', url: 'https://intelgrader.com' },
    };
    const publisher = { '@type': 'Organization', name: 'IntelGrader', url: 'https://intelgrader.com', logo: { '@type': 'ImageObject', url: 'https://intelgrader.com/assets/logo-wordmark.png' } };

    // HowTo: extract numbered "## Step" or H2 headings as steps
    if (schemaType === 'HowTo') {
      const stepMatches = [...page.body_markdown.matchAll(/^##\s+(?:Step\s*\d+[:.]?\s*)?(.+)$/gm)];
      const steps = stepMatches
        .map((m) => m[1].trim())
        .filter((t) => !/^(FAQ|Frequently Asked|TL;DR|Sources?|References?)/i.test(t))
        .slice(0, 10);
      return {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: page.title,
        description: page.description,
        image: page.image_url || 'https://intelgrader.com/og-image.png',
        author: authorObj,
        publisher,
        datePublished: page.published_at,
        dateModified: page.updated_at,
        step: steps.map((name, i) => ({
          '@type': 'HowToStep',
          position: i + 1,
          name,
        })),
      };
    }

    // Review: used on /vs/ pages — itemReviewed = the competitor product
    if (schemaType === 'Review') {
      const competitorSlug = page.slug.replace(/^vs\//, '');
      const competitorName = competitorSlug
        .split('-')
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
        .join(' ');
      return {
        '@context': 'https://schema.org',
        '@type': 'Review',
        name: page.title,
        itemReviewed: {
          '@type': 'SoftwareApplication',
          name: competitorName,
          applicationCategory: 'EducationalApplication',
        },
        author: authorObj,
        publisher,
        datePublished: page.published_at,
        dateModified: page.updated_at,
        reviewBody: page.description,
      };
    }

    // Default: Article
    return {
      '@context': 'https://schema.org',
      '@type': schemaType,
      headline: page.title,
      description: page.description,
      author: authorObj,
      publisher,
      datePublished: page.published_at,
      dateModified: page.updated_at,
      image: page.image_url || 'https://intelgrader.com/og-image.png',
      mainEntityOfPage: { '@type': 'WebPage', '@id': `https://intelgrader.com/${page.slug}` },
    };
  }, [page, author]);

  const breadcrumbLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://intelgrader.com/' },
      { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://intelgrader.com/blog' },
      { '@type': 'ListItem', position: 3, name: page.title, item: `https://intelgrader.com/${page.slug}` },
    ],
  }), [page]);

  const faqJsonLd = useMemo(() => {
    const faqs = extractFaqs(page.body_markdown);
    return buildFaqJsonLd(faqs);
  }, [page.body_markdown]);

  return (
    <>
      {/* JSON-LD rendered inline since Helmet is removed */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(personLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      {faqJsonLd && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />}

      {/* Reading progress bar */}
      <div className="reading-progress" aria-hidden="true">
        <div
          className="reading-progress__bar"
          style={{ transform: `scaleX(${readProgress})` }}
        />
      </div>

      {/* Back to top button */}
      {readProgress > 0.15 && (
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
          className="fixed bottom-6 right-6 z-40 w-11 h-11 rounded-xl bg-[var(--color-primary)] text-white shadow-lg hover:opacity-90 transition-opacity flex items-center justify-center"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path d="M10 15V5M5 10l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}

      <article className="max-w-7xl mx-auto px-6 py-16 md:py-24">
        {/* Breadcrumb */}
        <nav className="text-sm text-[var(--color-muted)] mb-8 max-w-3xl">
          <Link href="/" className="hover:text-[var(--color-primary)]">Home</Link>
          <span className="mx-2">/</span>
          <Link href="/blog" className="hover:text-[var(--color-primary)]">Blog</Link>
          {page.category && (
            <>
              <span className="mx-2">/</span>
              <span>{page.category}</span>
            </>
          )}
        </nav>

        {/* Header */}
        <header className="mb-12 max-w-4xl">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[var(--color-text)] tracking-tight leading-tight mb-6">
            {page.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-[var(--color-muted)]">
            {publishDate && <time dateTime={page.published_at || ''}>{publishDate}</time>}
            {page.reading_time && <span>{page.reading_time} min read</span>}
            <span>By {page.author}</span>
          </div>
          {page.image_url && (
            <Image
              src={page.image_url}
              alt={page.image_alt || page.title}
              className="w-full rounded-xl mt-8"
              width={1200}
              height={630}
              priority
            />
          )}
        </header>

        {/* 3-column layout (Bit.ai style): TOC left · content middle · CTA right */}
        <div className="lg:grid lg:grid-cols-[220px_minmax(0,1fr)_280px] lg:gap-10">
          {/* Sidebar TOC — LEFT (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <TableOfContents contentHtml={contentHtml} />
            </div>
          </aside>

          {/* Main content */}
          <div className="min-w-0">
            {/* Quick Summary Box (Bit.ai style multi-bullet) */}
            {tldr && (
              <aside className="mb-10 p-6 rounded-2xl border border-[rgba(66,143,233,0.25)] bg-[rgba(66,143,233,0.05)]">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xl">&#x1F4CB;</span>
                  <h2 className="font-heading text-xs font-bold uppercase tracking-wider text-[var(--color-primary)] m-0">
                    Quick Summary
                  </h2>
                </div>
                <div className="text-[0.95rem] leading-relaxed text-[var(--color-text-body)] [&_ul]:my-0 [&_ul]:pl-0 [&_ul]:list-none [&_li]:py-1">
                  {tldr.includes('\n') || tldr.startsWith('-') ? (
                    <div dangerouslySetInnerHTML={{ __html: marked.parse(tldr) as string }} />
                  ) : (
                    <p className="m-0">{tldr}</p>
                  )}
                </div>
              </aside>
            )}

            <div
              className="prose-content"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />

            {/* Author Bio Card */}
            <div className="author-card">
              <div className="author-card__avatar">{author.initials}</div>
              <div>
                <div className="author-card__name">{author.name}</div>
                <div className="author-card__bio">{author.bio}</div>
              </div>
            </div>

            {/* In-content CTA */}
            <CTACard variant="primary" />
          </div>

          {/* Sticky CTA card — RIGHT (desktop) */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 p-6 rounded-2xl border border-[var(--color-border)] bg-gradient-to-br from-[var(--color-primary-subtle)] to-[var(--color-surface)] shadow-sm">
              <h3 className="font-heading text-lg font-bold text-[var(--color-text)] leading-tight mb-2">
                Save hours every week
              </h3>
              <p className="text-sm text-[var(--color-muted)] leading-relaxed mb-4">
                IntelGrader grades essays, MCQs, and assignments in seconds. Free trial. No card needed.
              </p>
              <ul className="text-xs text-[var(--color-text)] space-y-1.5 mb-5">
                <li>&#x2705; Saves 10+ hrs/week</li>
                <li>&#x2705; 98% accuracy</li>
                <li>&#x2705; Set up in minutes</li>
              </ul>
              <Link
                href="/book-demo"
                onClick={() => trackEvent('book_demo_click', 'CTA', 'blog_sidebar')}
                className="block w-full text-center px-4 py-2.5 rounded-lg bg-[var(--color-primary)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Try IntelGrader Free &rarr;
              </Link>
            </div>
          </aside>
        </div>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-16 pt-12 border-t border-[var(--color-border)]">
            <h2 className="font-heading text-2xl font-semibold text-[var(--color-text)] mb-8">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.slice(0, 3).map((post) => (
                <Link
                  key={post.slug}
                  href={`/${post.slug}`}
                  className="group p-5 rounded-xl border border-[var(--color-border)] hover:border-[var(--color-border-hover)] transition-colors bg-[var(--color-surface)]"
                >
                  {post.image_url && (
                    <Image
                      src={post.image_url}
                      alt={post.image_alt || post.title}
                      className="w-full h-36 object-cover rounded-lg mb-4"
                      width={400}
                      height={144}
                    />
                  )}
                  <h3 className="font-heading font-semibold text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors text-sm leading-snug">
                    {post.title}
                  </h3>
                  {post.reading_time && (
                    <p className="text-xs text-[var(--color-muted)] mt-2">{post.reading_time} min read</p>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </>
  );
};

export default BlogLayout;
